import { useState } from "react";
import { requestLink, getSession, clearSession } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Sign in (magic link). Optional in Phase 1: the app still works signed out. ──
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D", green:"#1E8A4C", danger:"#D93025",
};

export default function AuthScreen({ onClose }) {
  const existing = getSession();
  const [mode, setMode] = useState(existing && existing.email ? "account" : "form");
  const [accountEmail, setAccountEmail] = useState(existing && existing.email ? existing.email : "");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function send() {
    if (!valid || sending) return;
    setSending(true); setError("");
    try {
      await requestLink(email);
      setSentTo(email.trim().toLowerCase());
      setMode("sent");
    } catch (e) {
      setError(e.message || "Could not send the sign-in email.");
    }
    setSending(false);
  }

  function signOut() {
    clearSession();
    setAccountEmail("");
    setEmail("");
    setMode("form");
  }

  return (
    <div data-testid="auth-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} input{font-family:inherit}`}</style>

      <AppHeader
        variant="modal"
        title={mode === "account" ? "Account" : "Sign in"}
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"24px 20px",animation:"fadeIn 0.3s ease"}}>

        {mode === "account" && (
          <div style={{background:C.white,borderRadius:18,border:`1.5px solid ${C.border}`,padding:"22px 18px"}}>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.5,textTransform:"uppercase"}}>Signed in as</div>
            <div style={{fontSize:17,fontWeight:800,color:C.body,margin:"6px 0 18px",wordBreak:"break-all"}}>{accountEmail}</div>
            <button onClick={signOut} data-testid="sign-out-btn" style={{width:"100%",padding:"13px 0",borderRadius:13,border:`1.5px solid ${C.border}`,background:C.white,color:C.danger,fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              Sign out
            </button>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.5,marginTop:14}}>
              Signing out removes your session on this device. Your data stays safe on your account; sign back in any time with a fresh link.
            </div>
          </div>
        )}

        {mode === "form" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontSize:14,color:C.muted,lineHeight:1.6}}>
              Enter your email and we will send you a one-tap sign-in link. No password to remember.
            </div>
            <div>
              <input
                data-testid="auth-email-input"
                type="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                value={email}
                onChange={(e)=>setEmail(e.target.value)}
                onKeyDown={(e)=>{ if(e.key==="Enter") send(); }}
                placeholder="you@email.com"
                style={{width:"100%",padding:"14px 16px",fontSize:16,fontWeight:600,color:C.body,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:13,outline:"none"}}
              />
            </div>
            {error && <div style={{fontSize:13,fontWeight:600,color:C.danger}}>{error}</div>}
            <button
              data-testid="auth-send-btn"
              disabled={!valid || sending}
              onClick={send}
              style={{width:"100%",padding:"15px 0",borderRadius:14,border:"none",fontSize:15,fontWeight:800,fontFamily:"inherit",
                background:(!valid||sending)?C.bg:`linear-gradient(135deg,${C.blue},${C.blueDark})`,
                color:(!valid||sending)?C.muted:"#fff",cursor:(!valid||sending)?"default":"pointer"}}>
              {sending ? "Sending..." : "Email me a sign-in link"}
            </button>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.5,textAlign:"center"}}>
              Links expire in 15 minutes and can be used once.
            </div>
          </div>
        )}

        {mode === "sent" && (
          <div style={{display:"flex",flexDirection:"column",gap:16,textAlign:"center",alignItems:"center",paddingTop:8}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke={C.blueDark} strokeWidth="1.8"/><path d="M4 7l8 6 8-6" stroke={C.blueDark} strokeWidth="1.8" strokeLinejoin="round"/></svg>
            </div>
            <div style={{fontSize:18,fontWeight:900,color:C.body}}>Check your email</div>
            <div style={{fontSize:14,color:C.muted,lineHeight:1.6,maxWidth:320}}>
              We sent a sign-in link to <span style={{fontWeight:800,color:C.body,wordBreak:"break-all"}}>{sentTo}</span>. Open it on this device to finish signing in.
            </div>
            <button onClick={()=>{ setMode("form"); setError(""); }} style={{background:"none",border:"none",color:C.blueDark,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:4}}>
              Use a different email
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
